import {
  createFileRoute,
  Navigate,
  useNavigate,
} from '@tanstack/react-router'
import {
  Building,
  CalendarRange,
  ClipboardCheck,
  IdCard,
  IdCardLanyard,
  Megaphone,
  MessageSquare,
  Plane,
  PlaneTakeoff,
  Route as RouteIcon,
  TowerControl,
  Users,
  type LucideIcon,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { useIsDesktop } from '@/components/ui/use-mobile'
import { Button } from '@/components/ui/button'
import landingPlaneBg from '@/assets/landing-plane.jpg'
import pronteerLogoFull from '@/assets/pronteer-logo-full.svg'
import quotesGlyph from '@/assets/quotes.svg'

const WEB_SIGNUP_URL = `${import.meta.env.VITE_WEB_URL}/?productId=pronteer_annual_web`

export const Route = createFileRoute('/')({
  component: IndexRoute,
})

const FEATURES: { icon: LucideIcon; label: string }[] = [
  { icon: CalendarRange, label: 'Schedule' },
  { icon: RouteIcon, label: 'Trips' },
  { icon: IdCard, label: 'Documents' },
  { icon: IdCardLanyard, label: 'Crew' },
  { icon: Users, label: 'PAX' },
  { icon: Megaphone, label: 'Announcements' },
  { icon: TowerControl, label: 'Airports' },
  { icon: Building, label: 'FBO Details' },
  { icon: Plane, label: 'Aircraft' },
  { icon: MessageSquare, label: 'Chat' },
  { icon: PlaneTakeoff, label: 'Flight Updates' },
  { icon: ClipboardCheck, label: 'Tasks & Checklists' },
]

function IndexRoute() {
  const { isLoaded, isSignedIn } = useAuth()
  const isDesktop = useIsDesktop()

  if (!isLoaded) {
    return null
  }

  if (isSignedIn) {
    // Desktop is the operations-only console — no Home route.
    return <Navigate to={isDesktop ? '/operations' : '/home'} />
  }

  return isDesktop ? <DesktopLanding /> : <MobileLanding />
}

function MobileLanding() {
  const navigate = useNavigate()

  return (
    <div className="h-full w-full relative flex flex-col justify-between overflow-hidden">
      {/* Full-bleed photo backdrop — fixed so it escapes the centered #root
          column (max-width in index.css) and reaches the screen edges on
          iPad. The photo is light regardless of theme, so this screen is
          theme-locked light with hardcoded colors instead of tokens. */}
      <div className="fixed inset-0 bg-white" aria-hidden>
        <img
          src={landingPlaneBg}
          alt=""
          className="absolute inset-0 size-full object-cover object-center"
        />
      </div>

      {/* Logo + tagline. The max-w + mx-auto column is a no-op on phones but
          keeps the content centered on iPad, where the viewport is wider than
          the 400px content column (496 = 400 + 2×48px padding). */}
      <div className="relative mx-auto w-full max-w-[496px] px-12 pt-[max(calc(var(--safe-top)+16px),48px)]">
        <img
          src={pronteerLogoFull}
          alt="Pronteer"
          className="h-[54px] w-auto"
        />
        <p className="mt-[52px] text-[34px] font-normal leading-[44px] tracking-[-1px] text-[#0a0a0a]">
          Business Aviation
          <br />
          Management
          <br />
          <span className="italic font-semibold text-[#801b69]">
            Simplified
          </span>
        </p>
      </div>

      {/* CTAs — same centered column as the tagline block above. */}
      <div className="relative mx-auto w-full max-w-[496px] px-12 pb-[calc(var(--safe-bottom)+32px)]">
        <div className="flex w-full flex-col gap-4">
          <LandingCtas navigate={navigate} />
        </div>
      </div>
    </div>
  )
}

function DesktopLanding() {
  const navigate = useNavigate()

  return (
    <div className="min-h-dvh w-full bg-[#f5f5f4] grid place-items-center p-4">
      {/* Two 640px columns — the whole card caps at 1280px, never full viewport. */}
      <div className="flex w-full max-w-[1280px]">
        {/* Left panel — the mobile splash recomposed as a card */}
        <div className="relative h-[852px] w-1/2 overflow-hidden rounded-l-[32px] bg-white">
          <img
            src={landingPlaneBg}
            alt=""
            className="absolute inset-x-0 bottom-0 h-[708px] w-full object-cover object-center"
            aria-hidden
          />
          <div
            className="absolute inset-0 bg-[linear-gradient(180deg,#fff_20.19%,rgba(255,255,255,0)_78.37%,#fff_98.56%)]"
            aria-hidden
          />
          <div className="absolute inset-0 mx-auto w-[329px]">
            <div className="pt-14">
              <img
                src={pronteerLogoFull}
                alt="Pronteer"
                className="h-[54px] w-auto"
              />
              <p className="mt-12 text-[34px] font-normal leading-[41px] tracking-[-1px] text-[#0a0a0a]">
                Business Aviation
                <br />
                Management
                <br />
                <span className="italic font-semibold text-[#801b69]">
                  Simplified
                </span>
              </p>
            </div>
            <div className="absolute inset-x-0 bottom-16 flex flex-col gap-4">
              <LandingCtas navigate={navigate} />
            </div>
          </div>
        </div>

        {/* Right panel — plum gradient wing */}
        <div className="flex h-[852px] w-1/2 flex-col items-center justify-center gap-20 rounded-r-[32px] bg-[linear-gradient(rgba(0,0,0,0.2),rgba(0,0,0,0.2)),linear-gradient(180deg,#6d225c_54.33%,#521945_100%)] px-[59px] py-[88px]">
          <p className="text-center text-base leading-normal text-white/80">
            Your flight department deserves better than chat groups and
            spreadsheets. Pronteer brings crew, trips, documents and
            communication into one place, with complete visibility and control.
          </p>

          <div className="flex w-full flex-col items-center gap-4 rounded-[24px] bg-[#fafafa]/[0.03] py-10">
            <img src={quotesGlyph} alt="" className="h-[18px] w-auto" aria-hidden />
            <p className="max-w-[460px] text-center text-lg text-white">
              “This App is a game changer for Crew Management! Love it!”
            </p>
            <p className="text-sm text-white/80">Lauryn</p>
          </div>

          <div className="grid grid-cols-3 gap-x-14 gap-y-10 opacity-80">
            {FEATURES.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2">
                <Icon className="size-7 shrink-0 text-white" />
                <span className="whitespace-nowrap text-[15px] text-white">
                  {label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function LandingCtas({
  navigate,
}: {
  navigate: ReturnType<typeof useNavigate>
}) {
  return (
    <>
      <Button
        size="lg"
        className="w-full h-[45px] rounded-[8px] bg-[#801b69] hover:bg-[#801b69]/90 active:bg-[#801b69]/80 text-[#fafafa] text-sm font-semibold"
        onClick={() => {
          window.open(WEB_SIGNUP_URL, '_blank', 'noopener,noreferrer')
        }}
      >
        Create Account
      </Button>
      <Button
        size="lg"
        className="w-full h-[45px] rounded-[8px] bg-[#0a0a0a] hover:bg-[#0a0a0a]/90 active:bg-[#0a0a0a]/80 text-[#fafafa] text-sm font-semibold"
        onClick={() => navigate({ to: '/signin' })}
      >
        Sign In
      </Button>
    </>
  )
}
